/* tslint:disable:template-cyclomatic-complexity max-file-line-count */

import { SafeUrl } from '@angular/platform-browser/src/security/dom_sanitization_service';

export interface IAppCategoryLayer {
    displayOrder?: number;
    category: ICategory;
    categoryId: number;
    layer: ILayer;
    layerId: number;
}

export interface ICategory {
    displayOrder?: number;
    id: number;
    name: string;
    parentId: number;
}

export interface IGroup {
    name: string;
    layers: ILayer[];
    groupId?: string;
    isOpen?: boolean;
    splitThemeJSON?: string;
}

export interface IMenuObject {
    name: string;
    selector: string;
}

export interface IMapcoreSubLayer extends __esri.Sublayer {
    name?: string;
    mapcoreProps?: IMapcoreProps;
    parent: IMapcoreMapImageLayer;
}

export interface IMapcoreFeatureLayer extends __esri.FeatureLayer {
    mapcoreProps?: IMapcoreProps;
}

export interface IMapcoreFeatureProps extends __esri.FeatureLayerProperties {
    mapcoreProps?: IMapcoreProps;
}

export interface IMapcoreGraphicsLayer extends __esri.GraphicsLayer {
    mapcoreProps?: IMapcoreProps;
}

export interface IMapcoreDynamicLayer extends __esri.BaseDynamicLayer {
    mapcoreProps?: IMapcoreProps;
}

export interface IMapcoreMapImageLayer extends __esri.MapImageLayer {
    mapcoreProps?: IMapcoreProps;
    sublayers: __esri.Collection<IMapcoreSubLayer>;
}

export interface IMapcoreGroupLayer extends __esri.GroupLayer {
    mapcoreProps?: IMapcoreProps;
    layers: __esri.Collection<MapCoreLayer>;
}

export interface IMCMapView extends __esri.MapView {
    mapcoreViewProps?: IMapCoreMap;
    resetMapProps(map?: any): void;
    addMapcoreLayerToMap(layerId: number, query?: IQuery): void;
}

interface ILayerSource {
    createdDate?: string;
    isActive?: boolean;
    lastEditedDate?: string;
    layerId?: number;
    sourceType?: string;
    uniqueName?: string;
    zoomScaleFilterId?: number;
}

export interface ILayer {
    columns?: ILayerColumn[];
    hoverColumnList?: string;
    description?: string;
    featureEndpoints?: any[];
    filter?: string;
    geometryTypes?: string[];
    graphicsJSON?: string;
    groupParent?: IGroup;
    id?: number;
    isGroupLayer?: boolean;
    isGroupParent?: boolean;
    isGroupLayerExpanded?: boolean;
    isSystem?: boolean;
    labelingInfo?: any[];
    labelingInfoJSON?: string;
    name?: string;
    opacity?: number;
    parentId?: number;
    parentQuery?: IQuery;
    query?: ISaveableQuery;
    queryJSON?: string;
    recordCount?: number;
    sources?: ILayerSource[];
    subLayers?: ILayer[];
    systemQueryJSON?: string;
    renderers?: any[];
    renderersJSON?: string;
    tables?: ITable[];
    typeId?: LayerTypeId;
    url?: string;
    hashedUrl?: string;
    visible?: boolean;
    categories?: ICategory[];
    lastEditedDate?: Date;
    joinColumns?: ILayerColumn[];
    childColumns?: ILayerColumn[];
    groupParentName?: string;
    groupParentUniqueId?: string;
    exportJoinFilterParams?: {
        exportEnabled?: boolean;
        exportJoinColumns?: any[];
    };
    isAOI?: boolean;
    isPETL?: boolean;
    permissions?: ILayerPermission[];
    userLayers?: {
        accessLevel: AccessLevel;
        userId: number;
    }[];
    docSources?: IDocumentSource[];
    originalMinScale?: number;
    originalMaxScale?: number;
    routingDetail?: IRoutingDetail;
    routingDetailJSON?: string;
    splitThemeLabel?: string;
    splitThemeJSON?: string;
}

export interface IMapcoreProps extends ILayer {
    filterOperator?: string;
    groupParent?: IGroup;
    guid?: string;
    hoverEnabled?: boolean;
    identifyEnabled?: boolean;
    identifyNonVisibleEnabled?: boolean;
    idFromSavedLayer?: number;
    idFromSavedMap?: number;
    metaTableId?: number;
    spatialQueryParams?: ISpatialQueryParams;
    symbol?: string;
    tableNames?: string[];
    category?: string;
    lastEditedDate?: Date;
    simpleRenderers?: any[];
    tables?: ITable[];
    hideInLegend?: boolean;
    isExternal?: boolean;
    originalMinScale?: number;
    originalMaxScale?: number;
    applyColorToEntireLayer?: boolean;
    color?: string;
    dataTableArr?: { name: string; layerId: string; maxFetchRecord?: number }[];
    state?: any;
    esriLayerId?: string;
    heatmapMode?: HeatmapMode;
    heatmapRenderer?: __esri.HeatmapRenderer;
    renderer?: __esri.Renderer;
    needsRefresh?: boolean;
    showInPrintLegend?: boolean;
    fields?: __esri.Field[];
    isThemed?: boolean;
    tableEditableColumns?: ITableEditableColumn[];
    extraFilter?: any;
    geometryString?: string;
    classBreakLabels?: string;
    printRoutingDetail?: boolean;
    splitThemeLabel?: string;
    splitTheme?: ISplitThemeConfiguration;
}

export enum LayerTypeId {
    System = 1,
    Application = 2,
    User = 3,
    Map = 4
}

export enum HeatmapMode {
    On = 'on',
    Auto = 'auto',
    Off = 'off'
}

export interface ISplitThemeConfiguration {
    parentLayerId: number;
    originalOpacity: number;
    parentQuery: IQuery;
    childQuery: IQuery;
    baseSymbols?: __esri.Symbol[];
    visualVariables?;
    groupLayer?: IMapcoreGroupLayer;
    layerColumns: { name: string; alias: string }[];
    field: string;
    isDate: boolean;
    isNumeric: boolean;
    query: string;
    rendererType: IThemeType;
    numClasses: number;
    classBreaks?: IThemeClassBreak[];
}

export type IThemeType = 'unique-value' | 'class-break';

export interface IThemeClassBreak {
    selected: boolean;
    info: __esri.ClassBreakInfo;
    layer: QueryableMapCoreLayer;
    opacity: number;
    symbols: __esri.Symbol[];
}

export interface ISpatialQueryParams {
    primaryDynamicLayers?: IDynamicLayerSpatial[];
    primaryFeatureLayers?: ILayer[];
    joinedDynamicLayers?: IDynamicLayerSpatial[];
    joinedFeatureLayers?: ILayer[];
    joinedGeometries?: string[];
    isIntersect?: boolean;
    guid?: string;
    objectIds?: number[];
    processingType?: 'Default' | 'GPProcess' | 'OracleSDO';
    srMatch?: boolean;
}

export interface IDynamicLayerSpatial {
    id: string;
    sublayers: __esri.Sublayer[];
}

export interface ILayerPermission {
    id: number;
    name: string;
    action: string;
}

export interface ILayerSettings extends IMapcoreProps {
    zoomToLayer?: boolean;
    generateNewLayer?: boolean;
    parentQuery?: IQuery;
    geometryString?: string;
}

export interface ILayerColumn {
    alias: string;
    columnIdentifier?: IColumnIdentifier;
    columnIdentifierId?: number;
    databaseDataType: string;
    databaseColumnOrder: number;
    databaseDataLength?: number;
    isCommaSeparated: boolean;
    separatorCharacter?: string;
    domainValueBasedFilter?: string;
    name: string;
    distinctValues?: string[];
    domainValues?: IDomainValue[];
}

export interface IMetaTable {
    columns: IMetaTableColumn[];
}

export interface IMetaTableColumn {
    columnName: string;
    columnAlias: string;
    domainValues: IDomainValue[];
}

export interface IDomainValue {
    codeValue: string | number;
    longDescription: string;
    shortDescription: string;
}

export interface ITableColumn {
    tableId?: number;
    owner?: string;
    tableName?: string;
    name?: string;
    alias?: string;
    dataType?: string;
    dataLength?: number;
    domainSetId?: number;
    domainSetName?: string;
    columnIdentifier?: IColumnIdentifier;
    isCommaSeparated?: boolean;
    separatorCharacter?: string;
    domainValueBasedFilter?: string;
    databaseColumnOrder?: number;
    distinctValues?: string[];
    domainValues?: IDomainValue[];
}

export interface ILayerTableChild {
    id: number;
    parentId?: number;
    layerId?: number;
    tableId?: number;
    relationshipName: string;
    parentJoinKey?: string;
    joinKey?: string;
    columnList?: string[];
    templates?: ITemplate[];

    parentPrimaryKeyColumnName?: string;
    layerChildRelationshipName?: string;
    childParentKeyColumnName?: string;
    orderKeyColumnName?: string;
}

export interface ITemplate {
    id: number;
    sourceId: number;
    sourceType: string;
    type: string;
    functionType: string;
    format: string;
    layout: string;
}

export interface IColumnSetting extends ILayerColumn {
    average?: number;
    calculateAverage?: boolean;
    calculateSum?: boolean;
    displayName?: string;
    columnName?: string;
    footerText?: string;
    isNumeric?: boolean;
    sum?: number;
    visible?: boolean;
    width?: number;
    isUrl?: boolean;
}

export interface ISmallColumnSetting {
    name: string;
    visible: boolean;
    width: number;
    calculateAverage: boolean;
    calculateSum: boolean;
}

export interface IColumnIdentifier {
    id?: number;
    name?: string;
}

export interface IColumnSettingsStorage {
    columnSettings: ISmallColumnSetting[];
    enableCustom: boolean;
    filterSettings?: IFilterSetting[];
    showAll: boolean;
    sortBy: ColumnSortOrder;
    sortSettings?: ISortSetting[];
}

export interface IFilterSetting {
    field: string;
    operator: string;
    value: string;
}

export interface ISortSetting {
    descending: boolean;
    field: string;
    sortOrder?: number;
}

export interface ILayerCatalogItem {
    displayOrder?: number;
    expanded?: boolean;
    name?: string;
    id?: number;
    layer?: ILayer;
    layerCatalogItems?: Array<ILayerCatalogItem>;
    isSelected?: boolean;
    ispetl?: boolean;
    description?: string;
}

export interface IMapCoreMap {
    /**
     * The ID for this map
     */
    id: number;
    name: string;
    description: string;
    data?: string;
    isSystem: boolean;
    isPublic: boolean;
    accessLevel?: AccessLevel;
    sharedWith?: { userId: number }[];
    createdDate?: Date;
    systemCategoryId?: number;
    systemCategoryName?: string;
    mapCategoryId?: number;
    mapCategoryName?: string;
    applicationID: number;
    lastEditedDate?: Date;
    owner?: string;
    layers?: IMapcoreSubLayer[];
    parentFolderId?: number;
    extent?: any;
    extentJSON?: string;
    tooltip?: boolean;
    selected?: boolean;
    basemap?: string;
}

export interface IMergeMapData {
    layers: [{ id: number; visible: boolean }];
    paramConfig: IMapQueryConfiguration[];

}

export interface IMergeMapDataLayer {
    name: string;
    id: number;
    description: string;
    isChecked?: boolean;
}

export interface IPointStyle {
    name: string;
    type?: string;
    value?: MarkerStyle;
    json?: {
        angle: number;
        xoffset: number;
        yoffset: number;
        type: string;
        imageData: string;
        contentType: string;
        width: number;
        height: number;
    };
    path?: string;
    pathSize?: number;
    size?: number;
}

export interface IEmailItem {
    toAddresses: string[];
    subject: string;
    body: string;
    fromAddress?: string;
    fromName?: string;
    cc?: string;
    bcc?: string;
    isHtml?: boolean;
}

export interface IExportParameters {
    fieldNames?: string[];
    sort?: string;
    layerColumns?: ILayerColumn[];
    layerId?: number;
    query?: IQuery;
    title?: string;
    includeLayerChildren?: boolean;
    childTableIds?: number[];
    fieldNameOrder?: string[];
    joinColumns?: ILayerColumn[];
    joinFieldNames?: string[];
    layers?: IMapcoreProps[];
    imageUrl?: string;
    data?: any[];
}

export interface IOperatorOption {
    operator?: string;
    displayText?: string;
}

export interface IQuery {
    field?: string;
    name?: string;
    operator?: string;
    subqueries?: IQuery[];
    value?: string;
    isMultiple?: boolean;
    multiValue?: string[];
    separatorCharacter?: string;
    childTableRecord?: IChildRecord;
    childTableQuery?: IQuery;
    joinRecord?: ILayerRMS;
    joinTableQuery?: IQuery;
    primaryLayerRMSKeys?: ILayerRMS;
    isHiddenFilter?: false;
    dateFormat?: string;
}

export interface IExtractedQuery {
    field?: string;
    operator?: string;
    value?: string;
    multiValue?: string[];
    primaryLayerRMSKeys?: ILayerRMS;
    districtNumber?: string;
}
export interface ISaveableQuery extends IQuery {
    spatialQueryParams?: ISpatialQueryParams;
    parentQuery?: IQuery;
}

export interface IQueryFind {
    field?: string | RegExp;
    name?: string | RegExp;
    operator?: string | RegExp;
    value?: string | RegExp;
}

export interface ISearchCriteria {
    address?: string;
    assetIdType?: string;
    columnName?: string;
    congressionalDistrictNumber?: string;
    countyCode?: string;
    countyCodes?: string[];
    data?: any;
    displayName?: string;
    districtNumber?: string;
    districtNumbers?: string[];
    houseDistrictNumber?: string;
    ids?: string[];
    municipalityCode?: string;
    schoolDistrictName?: string;
    latitude?: number;
    longitude?: number;
    layerId?: number;
    planningPartnerShortName?: string;
    query?: IQuery;
    segBgn?: string;
    segEnd?: string;
    senateDistrictNumber?: string;
    stateRouteNumber?: string;
    title?: string;
    type?: string;
    zip?: string;
    offset?: string;
    magicKey?: string;
}

export interface ISearchResult {
    id?: number;
    layerId?: number;
    magicKey?: string;
    name?: string;
    objectId?: number;
    objectIds?: number[];
    primaryText?: string;
    secondaryText?: string;
    type?: string;
    tertiaryText?: string;
    layerTitle?: string;
    description?: string;
}

export interface ISearchResultType {
    name?: string;
    pluralDisplayName?: string;
    count?: number;
}

export interface ISpatialReference {
    wkt?: string;
}

export interface ITable {
    id: number;
    geometryType?: string;
    name?: string;
    owner?: string;
    metaTable?: { id: number };
    spatialReference?: ISpatialReference;
    columns?: ITableColumn[];
    records?: string[][];
    tableEditableColumns?: ITableEditableColumn[];
}

export interface IChildRecord {
    id: number;
    childOwner?: string;
    parentLayerId: string;
    parentPrimaryKeyColumnName?: string;
    layerChildRelationshipName?: string;
    childTableName?: string;
    childParentKeyColumnName?: string;
    orderKeyColumnName?: string;
}

export interface ILayerRMS {
    id: number;
    tableId: number;
    tableIds?: string;
    tableName: string;
    layerDisplayName: string;
    tableDisplayName: string;
    geometryType: string;
    ctyCodeColName: string;
    strtnoColName: string;
    jurisColName: string;
    segBgnColName: string;
    segEndColName: string;
    offsetBgnColName: string;
    offsetEndColName: string;
    sideIndColName: string;
    isOffsetNumber: boolean;
}

export interface IDateFormat {
    oracleFormat: string;
    pattern: RegExp;
}

export interface IUserFolder {
    applicationId?: number;
    category?: UserFolderCategory;
    createdDate?: string;
    displayOrder?: number;
    id?: number;
    lastEditedDate?: string;
    maps?: number[];
    name?: string;
    parentUserFolderId?: number;
    userId?: number;
}

export interface IUserMap {
    accessLevel: AccessLevel;
    sharedWith: {
        userId: number;
        accessLevel: AccessLevel;
    }[];
    map: {
        id: number;
        name: string;
        description: string;
        data?: string;
        isSystem: boolean;
        isPublic: boolean;
        accessLevel?: AccessLevel;
        sharedWith?: {
            userId: number;
            accessLevel: AccessLevel;
        }[];
        createdDate: Date;
        lastEditedDate: Date;
        owner?: string;
        parentFolderId?: number;
        selected?: boolean;
    };
}

export type UserFolderCategory = 'maps' | 'layers' | 'drawing-layers';

export interface IDistrict {
    id: number;
    districtNumber: string;
    counties?: {
        countyCode: string;
    }[];
}

export interface ICounty {
    id?: number;
    countyCode: string;
    name?: string;
    districtNumber?: string;
}

export interface IRMS {
    countyCode?: string;
    stateRoute?: string;
    segmentBegin?: string;
}

export interface IRMSClipRange {
    county: string;
    route: string;
    side: string;
    seg1: string;
    offset1: string;
    seg2: string;
    offset2: string;
}

export type MeasureState = 'disabled' | 'ready' | 'measuring' | 'measured';

export type ColumnSortOrder = 'alphabetical' | 'database' | 'custom';

export interface ILayerTypes {
    graphics: IMapcoreGraphicsLayer[];
    mapImage: IMapcoreMapImageLayer[];
    feature: IMapcoreFeatureLayer[];
    dynamic: IMapcoreDynamicLayer[];
}

export type MapCoreLayer = IMapcoreDynamicLayer | IMapcoreFeatureLayer | IMapcoreGraphicsLayer | IMapcoreMapImageLayer | IMapcoreGroupLayer;
export type QueryableMapCoreLayer = IMapcoreDynamicLayer | IMapcoreFeatureLayer | IMapcoreMapImageLayer;
export function isQueryableMapCoreLayer(layer: MapCoreLayer): layer is QueryableMapCoreLayer {
    return layer.type !== 'group' && layer.type !== 'graphics';
}

export interface IMCNotification {
    message: string;
    level: LogLevel;
    isPrivate: boolean;
    uniqueId?: number;
    alertClass?: string;
}

export enum LogLevel {
    /** All levels */
    Verbose,
    /** Designates finer-grained informational events than the DEBUG */
    Trace,
    /** Designates fine-grained informational events that are most useful to debug an application */
    Debug,
    /** Designates informational messages that highlight the progress of the application at coarse-grained level */
    Info,
    /** Designates potentially harmful situations */
    Warn,
    /** Designates error events that might still allow the application to continue running */
    Error,
    /** Designates very severe error events that will presumably lead the application to abort */
    Fatal
}

export enum Juris {
    State = 1,
    Turnpike = 2,
    StateAgency = 3,
    NonStateNonFederalAid = 4,
    NonStateFederalAid = 5,
    TollBridge = 6,
    LocalRoad = 8
}

export interface IDefaultPrintTemplate {
    label?: string;
    format: 'pdf' | 'png32' | 'png8' | 'jpg' | 'gif' | 'eps' | 'svg' | 'svgz';
    layoutOptions: {
        titleText?: string;
        authorText?: string;
        copyrightText?: string;
        scalebarUnit?: 'Miles' | 'Kilometers' | 'Meters' | 'Feet';
        legendLayers?: any[];
        customTextElements?: any[];
    };
    exportOptions?: {
        dpi: number;
    };
    title?: string;
    author?: string;
    copyright?: string;
    scalebarUnit?: string;
    selectedLayout?: IPrintLayout;
    basemap?: string;
}

export type LineStyle = 'dash'
    | 'dash-dot'
    | 'dot'
    | 'long-dash'
    | 'long-dash-dot'
    | 'long-dash-dot-dot'
    | 'none'
    | 'short-dash'
    | 'short-dash-dot'
    | 'short-dash-dot-dot'
    | 'short-dot'
    | 'solid';

export type MarkerStyle = 'circle' | 'square' | 'cross' | 'x' | 'diamond' | 'triangle' | 'path';

export type FillStyle = 'backward-diagonal'
    | 'cross'
    | 'diagonal-cross'
    | 'forward-diagonal'
    | 'horizontal'
    | 'none'
    | 'solid'
    | 'vertical';

export type HorizontalAlignment = 'left' | 'right' | 'center';

export type VerticalAlignment = 'baseline' | 'top' | 'middle' | 'bottom';

export type PrintLayout = 'map-only'
    | 'a3-landscape'
    | 'a3-portrait'
    | 'a4-landscape'
    | 'a4-portrait'
    | 'letter-ansi-a-landscape'
    | 'letter-ansi-a-portrait'
    | 'tabloid-ansi-b-landscape'
    | 'tabloid-ansi-b-portrait';

export interface IPrintLayout {
    /**
     * The pretty name of the template
     */
    name: string;
    /**
     * The width of the template (in inches). Can be fractional.
     */
    widthInches: number;
    /**
     * The height of the template (in inches). Can be fractional.
     */
    heightInches: number;
    id: number;
}

export interface IUserSetting {
    [key: string]: any;
    name?: string;
    value: any;
}

export type CRSList = {
    countyCode: string;
    direction: string;
    distance: number;
    latLong: { latitude: number; longitude: number };
    offset: string;
    routeName: string;
    segmentNumber: string;
    stateRouteNumber: string;
}[];

export type LocalCRSList = {
    countyCode: string;
    countyIXN: string;
    stateRouteNumber: string;
    streetName: string;
    streetNameIXN: string;
    stateRouteIXN: string;
    intersectionType: string;
}[];

export type VideoLogAction = 'continue' | 'prompt' | 'deny';

export type VideoLogPlayerState = 'play' | 'pause' | 'stop';

export interface IVideoLogImage {
    fileName: string;
    imageDate: string;
    imageName1: string;
    imageName2: string;
    imageName3: string;
    latitude: number;
    longitude: number;
    offset: string;
    sequenceNumber: number;
    url: string;
    lurl?: string;
    rurl?: string;
}

export type FirstOrLast = 'first' | 'last';

export interface IMapServer {
    name: string;
    url: string;
    displayOrder: number;
}

export interface IMapServerFolder {
    name: string;
    mapServerFolders: IMapServerFolder[];
    url: string;
    services: IMapService[];
    retrievedFromDisk?: boolean;
    folders?: string[];
}

export interface IMapService {
    name: string;
    url: string;
    type: string;
    serviceDescription: string;
    description: string;
    layers: IFeatureCatalogLayer[];
}

export interface IFeatureLayerCatalog {
    urlsWithErrors: string[];
    mapServers: IMapServerFolder[];
    loading: boolean;
    updatedOn?: string;
}

export interface IFeatureCatalogLayer {
    id: number;
    name: string;
    type: string;
    subLayerIds: number[];
}

export interface ISystemMessage {
    id: number;
    title: string;
    message: string;
    startDate?: Date;
    expirationDate?: Date;
    createDate?: Date;
    systemMessageCategoryId: number;
    systemMessageCategory?: ISystemMessageCategory;
}

export interface IUserSystemMessageStatus {
    systemMessageId: number;
    userId: number;
    isRead: boolean;
    isDismissed: boolean;
    isOutage?: boolean;
    show?: boolean;
    systemMessage: ISystemMessage;
}

export interface ISystemMessageCategory {
    id: number;
    categoryName: string;
    categoryShortName?: string;
}

export interface IMapQueryConfiguration {
    type: string;
    field: string;
    filter?: { [key: string]: string };
    label?: string;
    layers: number[];
    value?: any;
    options?: any[];
    columnMap?: {
        [key: number]: {
            fieldName: string;
            dependentFieldName?: string;
        };
    };
    countyOptionsColName?: string;
    dependentColLabel?: string;
    dependentField?: string;
    dependentOptionsTableNames?: string[];
    dependentOptions?: string[];
    districtOptionsColName?: string;
    primaryColLabel?: string;
    primaryOptionsTableNames?: string[];
    compareFullDate?: boolean;
    duplicateType?: boolean;
}

export interface IBuffer {
    graphic: __esri.Graphic;
    distance: number;
    distanceUnit: __esri.LinearUnits;
    area?: number;
    areaUnit?: __esri.ArealUnits;
    layer?: MapCoreLayer;
    title?: string;
}

export interface IWorkingQuery extends IQuery {
    disableDistinctValues?: boolean;
    disableValueEntry?: boolean;
    distinctValueColumnName?: string;
    distinctValues?: IDistinctValue[];
    filteredDistinctValues?: IDistinctValue[];
    filterDistinctOnPrevious?: boolean;
    previousFilterDistinctOnPreviousValue?: boolean;
    inputType?: string;
    operatorOptions?: IOperatorOption[];
    isMultiple?: boolean;
    multiValue?: string[];
    childTableQuery?: IWorkingQuery;
    joinTableQuery?: IWorkingQuery;
    subqueries?: IWorkingQuery[];
    loadingDistinct?: boolean;
    isHiddenFilter?: false;
    customValues?: string[];
}

export interface IDistinctValue {
    text: string;
    value: string;
}

export enum DocumentSources {
    ECS = 'ECS',
    PHOTO = 'PHOTO',
    VIDEO = 'TRAFFIC_VIDEO',
    FEATURE = 'FEATURE_ATTACHMENT'
}

export interface IDocumentBase extends Record<string, any> {
    title?: string;
    fileName?: string;
    description?: string;
    mimeType?: string;
    url?: string | SafeUrl;
}

export type IECSDocument = {
    source: DocumentSources.ECS;
    documentId: string;
    documentClass: string;
    documentType: string;
    size: number;
    creationDate: string;
    lastModificationDate: string;
} & IDocumentBase;

export type IPhotoDocument = {
    source: DocumentSources.PHOTO;
} & IDocumentBase;

export type IVideoDocument = {
    source: DocumentSources.VIDEO;
    thumbnail: string | SafeUrl;
} & IDocumentBase;

export type IFeatureDocument = {
    source: DocumentSources.FEATURE;
    parentObjectId?: number;
    keywords?: string;
    exifInfo?: string;
} & IDocumentBase;

export type MapCoreDocument = IECSDocument | IPhotoDocument | IVideoDocument | IFeatureDocument;

export interface IDocumentSource {
    id: number;
    type: DocumentSources;
    url?: string;
    fields: string;
    template: string;
    options?: string;
}

export interface IAttribute {
    columnName: string;
    dbOrder: number;
    displayName: string;
    value: string | number;
    type?: AttributeType;
    domainValue?: string;
    hide?: boolean;
}

export interface ITrafficCameraProperties {
    distance: number | null;
    direction: string;
    mrm: number;
    id: string;
    jurisdiction: string;
    route: string;
    description: string;
    name: string;
    deviceid: number;
    rtmpUrl: string;
    iosUrl: string;
    rtspUrl: string;
    httpsUrl: string;
    clspsUrl: string;
    imageUrl: string;
    active: boolean;
    problemStream: boolean;
}

export interface IHistogram {
    bins: {
        count: number;
        minValue: number;
        maxValue: number;
    }[];
    minValue: number;
    maxValue: number;
}

export interface IClassBreakData {
    ClassBreakInfos: __esri.ClassBreakInfo[];
    Histogram: IHistogram;
    IsNumericData: boolean;
}

export interface IClassBreakDataResult {
    classBreakData: IClassBreakData;
}

export interface ITableEditableColumn {
    id: number;
    name: string;
    displayOrder: number;
    inputType?: IInputType;
    isEditable: boolean;
    isInsertable: boolean;
    validationType?: IValidationType;
    tableId: number;
}

export interface IInputType {
    name: string;
    attr: string;
    geometryType: string;
    dependentCategory: string;
    showFeatureSearch: string;
    categoryColName: string;
    showFeatureId: string;
    featureCategories: string[];
    format: string;
    datePicker?: boolean;
    includeTimeStamp?: boolean;
    segColName: string;
    featureSubcategories: string[];
    displayText: string;
}

export interface IValidationType {
    required: boolean;
}

export interface IRMSOffset {
    countyCode: string;
    countyName: string;
    stateRouteNumber: string;
    routeName: string;
    segmentNumber: string;
    offset: string;
    direction: string;
    juris: string;
    sideIndicator: string;
    distance: number;
    rmsKey: string;
}

export interface IColumnIdentifer {
    name?: string;
    description?: string;
    id: number;
}

export interface ILayerLoadStatusChangeDetail {
    layerId: string;
    updating: boolean;
}

export interface IRoutingDetail {
    distance: number;
    distanceInMiles: number;
    unit: string;
    pointBarriers: any;
    stops: any;
    directions: any;
}

export interface ISymbolChangeDetail {
    layerId: string;
    symbols: __esri.Symbol[];
    opacity: number;
}

export function isClassBreaksRenderer(renderer: __esri.Renderer): renderer is __esri.ClassBreaksRenderer {
    return renderer.type === 'class-breaks';
}
export function isUniqueValueRenderer(renderer: __esri.Renderer): renderer is __esri.UniqueValueRenderer {
    return renderer.type === 'unique-value';
}
export function isSimpleRenderer(renderer: __esri.Renderer): renderer is __esri.SimpleRenderer {
    return renderer.type === 'simple';
}
export function isHeatmapRenderer(renderer: __esri.Renderer): renderer is __esri.HeatmapRenderer {
    return renderer.type === 'heatmap';
}
export function isClassBreakInfo(infos: (__esri.ClassBreakInfo | __esri.UniqueValueInfo)[]): infos is __esri.ClassBreakInfo[] {
    return infos !== null && infos.length && (<__esri.ClassBreakInfo>infos[0]).maxValue !== undefined;
}
export function isUniqueValueInfo(infos: (__esri.ClassBreakInfo | __esri.UniqueValueInfo)[]): infos is __esri.UniqueValueInfo[] {
    return infos !== null && infos.length && (<__esri.UniqueValueInfo>infos[0]).value !== undefined;
}

export function isSymbolPropertiesWithOutline(symbolProperties: __esri.SimpleMarkerSymbolProperties | __esri.SimpleFillSymbolProperties | __esri.SimpleLineSymbolProperties): symbolProperties is __esri.SimpleMarkerSymbolProperties | __esri.SimpleFillSymbolProperties {
    return symbolProperties.type === 'simple-fill' || symbolProperties.type === 'simple-marker';
}

export function isSymbolWithStyle(symbol: __esri.Symbol): symbol is __esri.SimpleFillSymbol | __esri.SimpleMarkerSymbol {
    return symbol.type === 'simple-fill' || symbol.type === 'simple-marker' || symbol.type === 'simple-line';
}
export function isSymbolWithOutline(symbol: __esri.Symbol): symbol is __esri.SimpleFillSymbol | __esri.SimpleMarkerSymbol {
    return symbol.type === 'simple-fill' || symbol.type === 'simple-marker';
}

export function isSymbolWithWidth(symbol: __esri.Symbol): symbol is __esri.SimpleLineSymbol {
    return symbol.type === 'simple-line';
}

export function isSymbolWithSize(symbol: __esri.SimpleLineSymbol | __esri.SimpleFillSymbol | __esri.SimpleMarkerSymbol): symbol is __esri.SimpleMarkerSymbol {
    return symbol.type === 'simple-marker';
}

export function isPictureMarkerSymbol(symbol: __esri.Symbol): symbol is __esri.PictureMarkerSymbol {
    return symbol.type === 'picture-marker';
}

export function isDynamicDataLayerSource(source: __esri.DynamicMapLayer | __esri.DynamicDataLayer): source is __esri.DynamicDataLayer {
    return source.type === 'data-layer';
}

export function isMapImageLayer(source: __esri.Layer): source is __esri.MapImageLayer {
    return source.type === 'map-image';
}

export function isGraphicsLayer(source: __esri.Layer): source is __esri.GraphicsLayer {
    return source.type === 'graphics';
}

export function isFeatureLayer(source: __esri.Layer): source is __esri.FeatureLayer {
    return source.type === 'feature';
}

export function isGeometryDataSource(dataSource: __esri.TableDataSource | __esri.QueryTableDataSource | __esri.RasterDataSource | __esri.JoinTableDataSource): dataSource is __esri.QueryTableDataSource {
    return dataSource.type === 'query-table';
}

export function isGroupLayer(layer: MapCoreLayer | __esri.Layer): layer is IMapcoreGroupLayer {
    return layer.type === 'group';
}

export function isTextSymbol(symbol: __esri.Symbol): symbol is __esri.TextSymbol {
    return symbol && symbol.type === 'text';
}

export function isCodedValueDomain(domain: __esri.Domain): domain is __esri.CodedValueDomain {
    return domain && domain.type === 'coded-value';
}
export function isRangeDomain(domain: __esri.Domain): domain is __esri.RangeDomain {
    return domain && domain.type === 'range';
}
export function isInheritedDomain(domain: __esri.Domain): domain is __esri.InheritedDomain {
    return domain && domain.type === 'inherited';
}

export function isPointGeometry(geometry: __esri.Geometry): geometry is __esri.Point {
    return geometry && geometry.type === 'point';
}

export type AccessLevel = 'OWNER' | 'VIEWER' | 'F';

export type AttributeType = 'small-integer'
    | 'integer'
    | 'single'
    | 'double'
    | 'long'
    | 'string'
    | 'date'
    | 'oid'
    | 'geometry'
    | 'blob'
    | 'raster'
    | 'guid'
    | 'global-id'
    | 'xml'
    | 'open-url'
    | 'download-url';

export class HeatmapConfig {
    public defaultOptions = {
        maxPixelIntensity: 100,
        minPixelIntensity: 1,
        blurRadius: 10,
        colorStops: [
            {
                ratio: 0,
                color: 'rgba(0, 255, 0 ,0)'
            },
            {
                ratio: 0.7,
                color: 'rgb(0, 255, 0)'
            },
            {
                ratio: 0.9,
                color: 'rgb(255, 0, 0)'
            }]
    };
}
